import { useEffect } from 'react';

const DEFAULT_SITE_URL =
    process.env.REACT_APP_SITE_URL || 'https://sportwice.com';
const DEFAULT_LOCALE = 'en';
const SUPPORTED_LOCALES = ['en', 'pl'] as const;

const getLocaleFromPath = (pathname: string) => {
    const firstSegment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    return SUPPORTED_LOCALES.includes(
        firstSegment as (typeof SUPPORTED_LOCALES)[number]
    )
        ? firstSegment
        : null;
};

const normalizePath = (path: string) => {
    if (!path || path === '/') {
        return '/';
    }

    const withLeadingSlash = path.startsWith('/') ? path : `/${path}`;
    return withLeadingSlash.replace(/\/+$/, '') || '/';
};

const withLocalePrefix = (basePath: string, locale: string) => {
    const normalizedPath = normalizePath(basePath);

    if (normalizedPath === '/') {
        return `/${locale}`;
    }

    return `/${locale}${normalizedPath}`;
};

type SeoConfig = {
    title: string;
    description: string;
    path?: string;
    noIndex?: boolean;
    canonicalUrl?: string;
};

const ensureMeta = (
    selector: string,
    attrName: 'name' | 'property',
    attrValue: string
) => {
    let meta = document.head.querySelector(selector) as HTMLMetaElement | null;
    if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attrName, attrValue);
        document.head.appendChild(meta);
    }
    return meta;
};

const setMetaContent = (
    selector: string,
    attrName: 'name' | 'property',
    attrValue: string,
    content: string
) => {
    const meta = ensureMeta(selector, attrName, attrValue);
    meta.setAttribute('content', content);
};

const setLink = (rel: string, href: string) => {
    let link = document.head.querySelector(
        `link[rel="${rel}"]`
    ) as HTMLLinkElement | null;
    if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
    }
    link.href = href;
};

const removeLink = (rel: string) => {
    const link = document.head.querySelector(
        `link[rel="${rel}"]`
    ) as HTMLLinkElement | null;

    if (link) {
        link.remove();
    }
};

const setAlternateLink = (hreflang: string, href: string) => {
    const selector = `link[rel="alternate"][hreflang="${hreflang}"]`;
    let link = document.head.querySelector(selector) as HTMLLinkElement | null;

    if (!link) {
        link = document.createElement('link');
        link.rel = 'alternate';
        link.hreflang = hreflang;
        document.head.appendChild(link);
    }

    link.href = href;
};

export default function useSeo({
    title,
    description,
    path = '/',
    noIndex = false,
    canonicalUrl,
}: SeoConfig) {
    useEffect(() => {
        const locale =
            getLocaleFromPath(window.location.pathname) || DEFAULT_LOCALE;
        const localizedPath = withLocalePrefix(path, locale);
        const url = canonicalUrl || `${DEFAULT_SITE_URL}${localizedPath}`;
        const ogLocale = locale === 'pl' ? 'pl_PL' : 'en_US';

        document.title = title;
        document.documentElement.lang = locale;

        setMetaContent(
            'meta[name="description"]',
            'name',
            'description',
            description
        );
        setMetaContent(
            'meta[name="robots"]',
            'name',
            'robots',
            noIndex ? 'noindex,nofollow' : 'index,follow'
        );

        setMetaContent(
            'meta[property="og:title"]',
            'property',
            'og:title',
            title
        );
        setMetaContent(
            'meta[property="og:description"]',
            'property',
            'og:description',
            description
        );
        setMetaContent(
            'meta[property="og:type"]',
            'property',
            'og:type',
            'website'
        );
        setMetaContent(
            'meta[property="og:site_name"]',
            'property',
            'og:site_name',
            'Sportwice'
        );
        setMetaContent('meta[property="og:url"]', 'property', 'og:url', url);
        setMetaContent(
            'meta[property="og:locale"]',
            'property',
            'og:locale',
            ogLocale
        );

        setMetaContent(
            'meta[name="twitter:card"]',
            'name',
            'twitter:card',
            'summary'
        );
        setMetaContent(
            'meta[name="twitter:title"]',
            'name',
            'twitter:title',
            title
        );
        setMetaContent(
            'meta[name="twitter:description"]',
            'name',
            'twitter:description',
            description
        );
        setMetaContent('meta[name="twitter:url"]', 'name', 'twitter:url', url);

        if (noIndex) {
            removeLink('canonical');
        } else {
            setLink('canonical', url);
        }

        if (!canonicalUrl) {
            const englishHref = `${DEFAULT_SITE_URL}${withLocalePrefix(
                path,
                'en'
            )}`;
            const polishHref = `${DEFAULT_SITE_URL}${withLocalePrefix(
                path,
                'pl'
            )}`;

            setAlternateLink('en', englishHref);
            setAlternateLink('pl', polishHref);
            setAlternateLink('x-default', englishHref);
        }
    }, [canonicalUrl, description, noIndex, path, title]);
}
